// Admin DOI dashboard — overview of DOI assignment status for accepted/published
// submissions. Shows summary counts, filter tabs, and a table of items.
import React, { useEffect, useState } from 'react';
import { getDoiDashboard, getActiveJournalSlug } from '../api/client';

const FILTERS = [
  { value: 'all', label: 'Hammasi', color: 'slate' },
  { value: 'assigned', label: 'Tayinlangan', color: 'emerald' },
  { value: 'pending', label: 'Kutilmoqda', color: 'amber' },
  { value: 'missing', label: 'Yo\'q', color: 'rose' },
  { value: 'failed', label: 'Xatolik', color: 'red' },
];

export default function DoiDashboardPage() {
  const [data, setData] = useState({ summary: {}, items: [] });
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getDoiDashboard({ doi_status: filter, journal_slug: getActiveJournalSlug() })
      .then((r) => setData(r.data))
      .catch(() => setData({ summary: {}, items: [] }))
      .finally(() => setLoading(false));
  }, [filter]);

  return (
    <div className="space-y-5 max-w-[1200px] mx-auto">
      <div>
        <h1 className="text-xl font-bold text-slate-900">DOI Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">
          Qabul qilingan va nashr etilgan maqolalarning Zenodo DOI tayinlash holati
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {['assigned', 'pending', 'missing', 'failed'].map((key) => {
          const f = FILTERS.find((x) => x.value === key);
          const colorClass = {
            emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
            amber: 'bg-amber-50 text-amber-700 border-amber-200',
            rose: 'bg-rose-50 text-rose-700 border-rose-200',
            red: 'bg-red-50 text-red-700 border-red-200',
          }[f.color];
          return (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`p-4 rounded-xl border text-left transition-colors ${colorClass} ${
                filter === key ? 'ring-2 ring-offset-1' : ''
              }`}
            >
              <div className="text-2xl font-bold">{data.summary?.[key] ?? '—'}</div>
              <div className="text-xs font-medium mt-1">{f.label}</div>
            </button>
          );
        })}
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
              filter === f.value
                ? 'bg-blue-600 text-white'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-400">Yuklanmoqda...</div>
        ) : data.items?.length === 0 ? (
          <div className="p-8 text-center text-slate-400">Filtr bo'yicha hech narsa topilmadi</div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">#</th>
                <th className="px-4 py-3 text-left font-semibold">Sarlavha</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3 text-left font-semibold">DOI</th>
                <th className="px-4 py-3 text-left font-semibold">Holat</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => (
                <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm font-mono text-slate-500">#{it.id}</td>
                  <td className="px-4 py-3 text-sm">
                    <a href={`#/submissions/${it.id}`} className="text-blue-600 hover:underline">
                      {it.title?.substring(0, 80)}{it.title?.length > 80 ? '...' : ''}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded font-mono">
                      {it.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono">
                    {it.doi ? (
                      <a href={it.zenodo_record_url || `https://doi.org/${it.doi}`} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">
                        {it.doi}
                      </a>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {it.doi ? (
                      <span className="text-emerald-600">✓ Tayinlangan</span>
                    ) : it.is_doi_pending ? (
                      <span className="text-amber-600">⏳ Kutilmoqda</span>
                    ) : it.zenodo_error_log ? (
                      <span className="text-red-600" title={it.zenodo_error_log}>
                        ⚠ Xatolik
                      </span>
                    ) : (
                      <span className="text-rose-500">— Yo'q</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
